/**
 * Gear page functions — loadout quantity, provision kit modal, image preview/upload.
 * Moved from inline <script> in gear.templ to bundled JS.
 */

/** Update gear quantity in a loadout via AJAX */
export async function updateGearLoadoutQuantity(gearId: number, loadoutId: number, quantity: number): Promise<void> {
    quantity = parseInt(String(quantity));
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    if (quantity > 99) quantity = 99;

    try {
        const response = await fetch('/ds/app/gear/' + gearId + '/loadouts/' + loadoutId, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ quantity }),
            credentials: 'include'
        });

        if (!response.ok) {
            console.error('Failed to update quantity');
        }
    } catch (error) {
        console.error('Failed to update quantity:', error);
    }
}

/** Load provision kits into the modal's select dropdown */
export async function loadProvisionKitsForModal(): Promise<void> {
    try {
        const response = await fetch('/api/provision-kits', {
            credentials: 'include'
        });
        if (!response.ok) return;

        const kits = await response.json();
        const select = document.getElementById('provision-kit-select') as HTMLSelectElement | null;
        if (!select) return;

        // Clear existing options except first
        select.innerHTML = "<option value=\"0\">-- Don't add to kit --</option>";

        // Add kit options
        kits.forEach(function (kit: { id: number; name: string }) {
            const option = document.createElement('option');
            option.value = String(kit.id);
            option.textContent = kit.name;
            select.appendChild(option);
        });
    } catch (error) {
        console.error('Failed to load provision kits:', error);
    }
}

/** Update image preview in the edit gear modal */
function updateEditGearImagePreview(): void {
    const imagePathInput = document.getElementById('edit-gear-imagepath') as HTMLInputElement | null;
    const imagePath = imagePathInput?.value || '';

    const preview = document.getElementById('edit-gear-image-preview') as HTMLImageElement | null;
    const placeholder = document.getElementById('edit-gear-image-placeholder');

    if (imagePath && preview && placeholder) {
        preview.src = '/' + imagePath;
        preview.classList.remove('hidden');
        placeholder.classList.add('hidden');
    } else if (preview && placeholder) {
        preview.src = '';
        preview.classList.add('hidden');
        placeholder.classList.remove('hidden');
    }
}

/** Preview a selected photo locally and upload it to the server */
export async function previewAndUploadGearPhoto(input: HTMLInputElement): Promise<void> {
    if (!input.files || !input.files[0]) return;

    const file = input.files[0];
    const preview = document.getElementById('edit-gear-image-preview') as HTMLImageElement | null;
    const placeholder = document.getElementById('edit-gear-image-placeholder');

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = function (e) {
        if (preview && placeholder && e.target?.result) {
            preview.src = e.target.result as string;
            preview.classList.remove('hidden');
            placeholder.classList.add('hidden');
        }
    };
    reader.readAsDataURL(file);

    // Get gear ID from hidden input (set by Datastar data-attr:value)
    const gearIdInput = document.getElementById('edit-gear-id') as HTMLInputElement | null;
    const gearId = gearIdInput?.value;
    if (!gearId || gearId === '0') {
        console.error('No gear ID found for photo upload');
        alert('Error: No gear ID found. Please close and reopen the edit modal.');
        return;
    }

    // Upload the photo
    const formData = new FormData();
    formData.append('photo', file);

    // Get the upload button to show loading state
    const uploadBtn = document.querySelector('#edit-gear-photo-input')?.parentElement?.querySelector('button');
    if (uploadBtn) uploadBtn.classList.add('loading');

    try {
        const response = await fetch('/ds/app/gear/' + gearId + '/photo', {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        if (!response.ok) {
            const error = await response.text();
            alert('Failed to upload photo: ' + error);
            return;
        }

        const result = await response.json();
        if (result.success && result.image_path) {
            // Photo is saved to DB — update preview to show the saved image
            if (preview) {
                preview.src = '/' + result.image_path;
            }
        }
    } catch (error) {
        console.error('Failed to upload photo:', error);
        alert('Failed to upload photo. Please try again.');
    } finally {
        // Clear loading state
        if (uploadBtn) uploadBtn.classList.remove('loading');
        // Clear file input
        input.value = '';
    }
}

/** Set up MutationObserver on the edit gear modal to update image preview when it opens */
export function initGearImagePreview(): void {
    const modal = document.querySelector('dialog[data-class\\:modal-open="$_showeditmodal"]');
    if (!modal) return;

    const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            if ((mutation.target as HTMLElement).classList.contains('modal-open')) {
                updateEditGearImagePreview();
            }
        });
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
}
